import { Order } from '../../domain/entities/order.entity';
import { OrderStatus } from '../../domain/value-objects/order-status.vo';
import { ContractId } from '../../domain/value-objects/contract-id.vo';
import { CreateOrderWithQualityControlDto, CreateOrderWithQualityControlDocumentDto, CreateOrderWithQualityControlAssetDto } from '../../application/dtos/create-order-with-quality-control.dto';

export interface OrderListItemApiResponse {
  id: string;
  code: string | null;
  portfolioName: string | null;
  clientName: string | null;
  clientReference: string | null;
  status: number;
  createdAt: string;
  version: number;
  lastVersionCreatedAt: string | null;
  lastVerificationCreatedAt: string | null;
  canCacelOrder: boolean | null;
  contractId?: number | null;
  isMigrated: boolean;
}

export interface OrderDetailApiResponse extends OrderListItemApiResponse {
  clientId?: string | null;
  portfolioId?: string | null;
  updatedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface OrderListPagedApiResponse {
  items: OrderListItemApiResponse[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItemsCount: number;
  orderField?: string;
  ascendingOrder: boolean;
}

export class OrderMapper {
  static toDomain(raw: OrderDetailApiResponse): Order {
    return {
      id: raw.id,
      code: raw.code ?? null,
      portfolioName: raw.portfolioName,
      clientName: raw.clientName ?? '',
      clientReference: raw.clientReference,
      canCacelOrder: raw.canCacelOrder ?? null,
      status: raw.status as OrderStatus,
      createdAt: new Date(raw.createdAt),
      version: raw.version ?? 0,
      lastVersionCreatedAt: raw.lastVersionCreatedAt ? new Date(raw.lastVersionCreatedAt) : null,
      lastVerificationCreatedAt: raw.lastVerificationCreatedAt ? new Date(raw.lastVerificationCreatedAt) : null,
      contractId: raw.contractId != null ? raw.contractId as ContractId : null,
      isMigrated: raw.isMigrated ?? false,
      clientId: raw.clientId ?? null,
      portfolioId: raw.portfolioId ?? null,
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : undefined,
      startedAt: raw.startedAt ? new Date(raw.startedAt) : null,
      finishedAt: raw.finishedAt ? new Date(raw.finishedAt) : null,
    };
  }

  static toDomainFromList(raw: OrderListItemApiResponse): Order {
    return {
      id: raw.id,
      code: raw.code ?? null,
      portfolioName: raw.portfolioName,
      clientName: raw.clientName ?? '',
      clientReference: raw.clientReference,
      status: raw.status as OrderStatus,
      canCacelOrder: raw.canCacelOrder ?? null,
      createdAt: new Date(raw.createdAt),
      version: raw.version ?? 0,
      lastVersionCreatedAt: raw.lastVersionCreatedAt ? new Date(raw.lastVersionCreatedAt) : null,
      lastVerificationCreatedAt: raw.lastVerificationCreatedAt ? new Date(raw.lastVerificationCreatedAt) : null,
      contractId: raw.contractId != null ? raw.contractId as ContractId : null,
      isMigrated: raw.isMigrated ?? false,
    };
  }

  static toDomainList(rawList: OrderListItemApiResponse[]): Order[] {
    return rawList.map((raw) => this.toDomainFromList(raw));
  }

  // ─── Presentation → API ──────────────────────────────────────────────────────

  static sortFieldToApi(field: string): string {
    const mapping: Record<string, string> = {
      'portfolioName': 'portfolio',
      'clientName': 'client',
      'status': 'status',
      'createdAt': 'createdat',
      'lastVersionCreatedAt': 'lastVersionCreatedAt',
      'lastVerificationCreatedAt': 'lastVerificationCreatedAt',
    };
    return mapping[field] ?? field.toLowerCase();
  }

  static dateFilterToIso(value: unknown): string {
    const date = value instanceof Date ? value : new Date(value as string);
    return date.toISOString();
  }

  static toCreateOrderWithQCDto(
    orderData: { clientId: string; clientReference: string; portfolioId: string },
    qualityControlData: { societyId: string; purposeId: string; appraisalReportNumber?: string | null; appraisalIssueDate?: Date | null; appraiserName?: string | null; origin?: number | null },
    documents: { file: File; documentTypeId: string | number | null }[],
    orderDocuments: { file: File; documentTypeId: string | number | null }[],
    assets?: CreateOrderWithQualityControlAssetDto[],
  ): CreateOrderWithQualityControlDto {
    const filteredDocs: CreateOrderWithQualityControlDocumentDto[] = documents
      .filter(d => d.documentTypeId != null)
      .map(d => ({ file: d.file, documentType: Number(d.documentTypeId) }));
    const filteredOrderDocs: CreateOrderWithQualityControlDocumentDto[] = orderDocuments
      .filter(d => d.documentTypeId != null)
      .map(d => ({ file: d.file, documentType: Number(d.documentTypeId) }));

    return {
      clientId: orderData.clientId,
      clientReference: orderData.clientReference || undefined,
      portfolioId: orderData.portfolioId || undefined,
      appraisalCompany: qualityControlData.societyId ? Number(qualityControlData.societyId) : undefined,
      appraisalPurpose: qualityControlData.purposeId ? Number(qualityControlData.purposeId) : undefined,
      appraisalReportNumber: qualityControlData.appraisalReportNumber ?? undefined,
      appraisalIssueDate: qualityControlData.appraisalIssueDate ?? undefined,
      appraiserName: qualityControlData.appraiserName ?? undefined,
      origin: qualityControlData.origin ?? null,
      documents: filteredDocs.length ? filteredDocs : undefined,
      orderDocuments: filteredOrderDocs.length ? filteredOrderDocs : undefined,
      assets: assets?.length ? assets : undefined,
    };
  }
}
